import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";

const STATUS_COLORS_LIGHT = {
  P: { bg: "#d4edda", color: "#155724" },
  A: { bg: "#f8d7da", color: "#721c24" },
  L: { bg: "#fff3cd", color: "#856404" },
  H: { bg: "#cce5ff", color: "#004085" },
  HL: { bg: "#e2d5f1", color: "#4a2c6a" },
  HD: { bg: "#e2d5f1", color: "#4a2c6a" },
  HP: { bg: "#d1ecf1", color: "#0c5460" },
  WO: { bg: "#e2e3e5", color: "#383d41" },
  WOA: { bg: "#f5c6cb", color: "#721c24" },
  "-": { bg: "#ffffff", color: "#999" },
};

const STATUS_COLORS_DARK = {
  P: { bg: "rgba(55, 214, 132, 0.22)", color: "#37d684" },
  A: { bg: "rgba(255, 109, 114, 0.22)", color: "#ff6d72" },
  L: { bg: "rgba(245, 177, 61, 0.22)", color: "#f5b13d" },
  H: { bg: "rgba(100, 180, 255, 0.22)", color: "#7ec8ff" },
  HL: { bg: "rgba(180, 140, 255, 0.22)", color: "#c4a8ff" },
  HD: { bg: "rgba(180, 140, 255, 0.22)", color: "#c4a8ff" },
  HP: { bg: "rgba(8, 145, 178, 0.28)", color: "#5ed0e8" },
  WO: { bg: "rgba(163, 167, 196, 0.16)", color: "#a3a7c4" },
  WOA: { bg: "rgba(255, 109, 114, 0.28)", color: "#ff8a8e" },
  "-": { bg: "transparent", color: "var(--ems-faint)" },
};

/**
 * Excel-style Monthly Basic Attendance Report (card view).
 * Sticky header on vertical scroll; No/Code/Name sticky on horizontal scroll.
 * Follows light/dark theme via EMS design tokens.
 */
function MonthlyBasicAttendanceCardView({
  organizationName = "",
  periodLabel = "",
  days = [],
  rows = [],
  headerExtra = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const statusColors = isDark ? STATUS_COLORS_DARK : STATUS_COLORS_LIGHT;

  const statusStyle = (code) => statusColors[code] || statusColors["-"];

  const legend = useMemo(
    () => [
      { code: "P", label: "Present" },
      { code: "A", label: "Absent" },
      { code: "L", label: "Leave" },
      { code: "H", label: "Holiday" },
      { code: "HD", label: "Half Day" },
      { code: "HP", label: "Holiday Present" },
      { code: "WO", label: "Week Off" },
    ],
    []
  );

  const totalCodes = ["P", "A", "L", "H", "HD", "HP", "WO"];

  const HEADER_ROW1_TOP = 0;
  const HEADER_ROW2_TOP = 24;

  const cell = {
    border: "1px solid var(--ems-border)",
    padding: "2px 4px",
    fontSize: 11,
    textAlign: "center",
    whiteSpace: "nowrap",
    color: "var(--ems-text)",
  };

  const stickyIdentity = (left, zIndex = 4) => ({
    position: "sticky",
    left,
    zIndex,
    background: "var(--ems-surface-2)",
    color: "var(--ems-text)",
  });

  const bodyStickyBg = "var(--ems-surface)";
  const dateHeaderBg = isDark
    ? "rgba(8, 145, 178, 0.18)"
    : "#eef5ff";
  const totalHeaderBg = "var(--ems-surface-2)";
  const weekdayBg = "var(--ems-surface-2)";
  const weekdayOffBg = isDark
    ? "rgba(163, 167, 196, 0.12)"
    : "#ececec";

  return (
    <div
      style={{
        border: "1px solid var(--ems-border)",
        borderRadius: 6,
        background: "var(--ems-surface)",
        color: "var(--ems-text)",
        padding: "8px 10px",
        margin: 0,
      }}
    >
      {headerExtra ? (
        <div style={{ marginBottom: 6 }}>{headerExtra}</div>
      ) : null}

      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "var(--ems-text)",
          }}
        >
          {organizationName || "Organization"}
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            marginTop: 2,
            color: "var(--ems-text)",
          }}
        >
          Monthly Basic Attendance Report
        </div>
        <div
          style={{
            fontSize: 13,
            color: "var(--ems-text-soft)",
            marginTop: 2,
          }}
        >
          {periodLabel}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 8,
          fontSize: 11,
          color: "var(--ems-text-soft)",
        }}
      >
        {legend.map((item) => {
          const s = statusStyle(item.code);
          return (
            <span
              key={item.code}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span
                style={{
                  minWidth: 22,
                  textAlign: "center",
                  fontWeight: 700,
                  background: s.bg,
                  color: s.color,
                  border: "1px solid var(--ems-border-strong)",
                  borderRadius: 3,
                  padding: "1px 4px",
                }}
              >
                {item.code}
              </span>
              {item.label}
            </span>
          );
        })}
      </div>

      <div
        style={{
          overflow: "auto",
          maxHeight: "calc(100vh - 200px)",
          border: "1px solid var(--ems-border)",
          borderRadius: 4,
        }}
      >
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "max-content",
            minWidth: "100%",
            background: "var(--ems-surface)",
            color: "var(--ems-text)",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  ...cell,
                  ...stickyIdentity(0, 6),
                  top: HEADER_ROW1_TOP,
                  minWidth: 40,
                }}
                rowSpan={2}
              >
                No
              </th>
              <th
                style={{
                  ...cell,
                  ...stickyIdentity(40, 6),
                  top: HEADER_ROW1_TOP,
                  minWidth: 90,
                }}
                rowSpan={2}
              >
                Employee Code
              </th>
              <th
                style={{
                  ...cell,
                  ...stickyIdentity(130, 6),
                  top: HEADER_ROW1_TOP,
                  textAlign: "left",
                  minWidth: 140,
                }}
                rowSpan={2}
              >
                Employee Name
              </th>
              {days.map((d) => (
                <th
                  key={`d-${d.date}`}
                  style={{
                    ...cell,
                    background: dateHeaderBg,
                    position: "sticky",
                    top: HEADER_ROW1_TOP,
                    zIndex: 3,
                  }}
                >
                  {d.label}
                </th>
              ))}
              {totalCodes.map((code) => (
                <th
                  key={`t-${code}`}
                  style={{
                    ...cell,
                    background: totalHeaderBg,
                    position: "sticky",
                    top: HEADER_ROW1_TOP,
                    zIndex: 3,
                  }}
                  rowSpan={2}
                >
                  {code}
                </th>
              ))}
            </tr>
            <tr>
              {days.map((d) => (
                <th
                  key={`w-${d.date}`}
                  style={{
                    ...cell,
                    background: d.is_weekoff ? weekdayOffBg : weekdayBg,
                    fontWeight: 500,
                    position: "sticky",
                    top: HEADER_ROW2_TOP,
                    zIndex: 3,
                    color: "var(--ems-text-soft)",
                  }}
                >
                  {d.weekday}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={3 + days.length + totalCodes.length}
                  style={{
                    ...cell,
                    padding: 16,
                    color: "var(--ems-muted)",
                  }}
                >
                  No attendance data for this period.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const statusByDate = {};
                (row.day_statuses || []).forEach((ds) => {
                  statusByDate[ds.date] = ds.status;
                });
                const totals = row.matrix_totals || {};

                return (
                  <tr key={row.organization_person_id || index}>
                    <td
                      style={{
                        ...cell,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        background: bodyStickyBg,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      style={{
                        ...cell,
                        position: "sticky",
                        left: 40,
                        zIndex: 2,
                        background: bodyStickyBg,
                      }}
                    >
                      {row.person_code || "-"}
                    </td>
                    <td
                      style={{
                        ...cell,
                        textAlign: "left",
                        fontWeight: 600,
                        position: "sticky",
                        left: 130,
                        zIndex: 2,
                        background: bodyStickyBg,
                      }}
                    >
                      {row.full_name || "-"}
                    </td>
                    {days.map((d) => {
                      const code = statusByDate[d.date] || "-";
                      const s = statusStyle(code);
                      return (
                        <td
                          key={`${row.organization_person_id}-${d.date}`}
                          style={{
                            ...cell,
                            background: s.bg,
                            color: s.color,
                            fontWeight: 700,
                          }}
                        >
                          {code}
                        </td>
                      );
                    })}
                    {totalCodes.map((code) => (
                      <td
                        key={`tot-${code}`}
                        style={{
                          ...cell,
                          fontWeight: 600,
                          background: "var(--ems-surface)",
                        }}
                      >
                        {totals[code] ?? 0}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MonthlyBasicAttendanceCardView;
